import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CalendarClock,
  Check,
  CreditCard,
  Gift,
  Mail,
  MapPin,
  Package,
  Truck,
  UserRound,
} from 'lucide-react';
import { fetchOrder, fetchOrderByToken, orderKeys } from '@/api/orders';
import { useAuth } from '@/providers/AuthProvider';
import { useBrand, usePlatform } from '@/providers/PlatformProvider';
import { formatAddressLines } from '@/lib/formatAddress';
import { forgetCheckoutToken, recallCheckoutToken } from '@/lib/checkoutToken';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { ButtonLink } from '@/components/ui/Button';
import { PaymentPanel } from '@/components/checkout/PaymentPanel';
import type { Order } from '@/types/order';

/**
 * Order confirmation — BUILD-PLAN §15, §41, §57, §75.
 *
 * ## An unpaid order is NOT a completed order
 *
 * This page used to open with a gold tick and the word "Thank you" the
 * instant an order record existed — before a penny had moved. That is not a
 * styling problem, it is the page telling the customer something untrue at
 * the exact moment they are deciding whether to trust the shop. Someone who
 * reads "Thank you", closes the tab and waits for a parcel has been misled by
 * us, and the order sits unpaid until it is cancelled.
 *
 * So the page has three distinct states and only the last one celebrates:
 *
 *   1. **awaiting** — the order is reserved, not bought. The heading says so,
 *      the step rail shows Payment as the current step, and the payment panel
 *      is the primary thing on the page. No tick, no thanks.
 *   2. **confirming** — the customer is back from the gateway but the webhook
 *      has not landed. Stated as in-progress, because a redirect is not proof
 *      (§27); only the webhook is.
 *   3. **confirmed** — payment verified. Now, and only now, the tick and the
 *      thanks, and the page reads as a receipt.
 *
 * The order is passed through navigation state so a guest still sees their
 * confirmation — the history endpoint is authenticated, so a guest has no way
 * to fetch it back. Signed-in customers additionally refetch, so a refresh or
 * a returning visit works.
 */
export function OrderConfirmationPage() {
  const { orderNumber = '' } = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const brand = useBrand();
  const { countries } = usePlatform();
  const reduceMotion = useReducedMotion();

  const passed = (location.state as { order?: Order } | null)?.order ?? null;

  /*
   | Router state first, stored token second.
   |
   | Straight after placing an order the router still holds it; after a return
   | from the payment gateway it does not, and only the stored copy remains.
   | Reading both means one code path covers arriving here either way.
   */
  const checkoutToken = passed?.checkoutToken ?? recallCheckoutToken(orderNumber);

  /*
   | Fetch it back, as whoever the customer is.
   |
   | Signed in: the authenticated history endpoint. A guest holding their
   | checkout token: the token-authorised one. Either way the page can rebuild
   | itself from the server after a return from the payment gateway, which is
   | the case that was broken — router state does not survive that trip, a
   | guest could not fetch, and the page fell back to "we cannot show the
   | details again" with no payment panel and therefore no way to settle.
   */
  const { data: fetched } = useQuery({
    queryKey: orderKeys.order(orderNumber),
    queryFn: () =>
      isAuthenticated
        ? fetchOrder(orderNumber)
        : fetchOrderByToken(orderNumber, checkoutToken ?? ''),
    enabled: orderNumber !== '' && (isAuthenticated || Boolean(checkoutToken)),

    /*
     | Keep asking while a payment is settling.
     |
     | Settlement is asynchronous — verification-on-return or a webhook — so
     | the page must re-read the order rather than trust its first answer.
     */
    refetchInterval: (query) =>
      query.state.data?.paymentStatus?.value === 'paid' ? false : 3000,
  });

  const order = fetched ?? passed;

  /*
   | Drop the stored capability once the order is settled.
   |
   | Declared HERE, above every early return in this component.
   |
   | It was originally placed further down, after the `if (!order)` branch —
   | so on the first render (order still loading) React saw fewer hooks than
   | on the render after it arrived, and threw "Rendered more hooks than
   | during the previous render". The confirmation page then died into an
   | error boundary at the exact moment a customer came back from paying.
   | Hooks must run unconditionally; the CONDITION belongs inside the effect.
   */
  useEffect(() => {
    if (order?.paymentStatus?.value === 'paid') forgetCheckoutToken(orderNumber);
  }, [order?.paymentStatus?.value, orderNumber]);

  usePageMetadata({ title: `Order ${orderNumber}`, description: 'Your Glanz Luxe order.' });

  /*
   * Motion is presentation only — every element is visible without it. A
   * reduced-motion visitor gets the same page, arrived at instantly.
   */
  const rise = (delay: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] as const },
        };

  /*
   * Reached without the order in hand — usually a refresh, which discards
   * navigation state. What to offer depends on who is asking: order history
   * is behind ProtectedRoute, so sending a guest there would bounce them to
   * a login they do not have (§86 — no dead ends).
   */
  if (!order) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center">
        <h1 className="font-display text-2xl text-ink">Order {orderNumber}</h1>

        <p className="mt-3 text-sm leading-relaxed text-muted">
          {isAuthenticated
            ? 'We could not load this order here. You will find it in your order history.'
            : 'Your order was placed — we cannot show the details again on this page, but your confirmation email has everything. Please quote this order number if you contact us.'}
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {isAuthenticated ? (
            <ButtonLink to="/orders" variant="secondary">
              View my orders
            </ButtonLink>
          ) : (
            <>
              {brand?.supportEmail && (
                <ButtonLink
                  to={`mailto:${brand.supportEmail}?subject=${encodeURIComponent(`Order ${orderNumber}`)}`}
                  variant="secondary"
                  external
                >
                  Email Support
                </ButtonLink>
              )}
              <ButtonLink to="/shop">Continue Browsing</ButtonLink>
            </>
          )}
        </div>
      </div>
    );
  }

  /*
   | Which of the three states this page is in.
   |
   | Driven by the ORDER's payment status, which is set by the webhook, not by
   | anything the browser can assert. `?payment=complete` in the URL only says
   | the customer came back from the gateway — it is a hint about what to say
   | while waiting, never evidence of payment.
   */
  const paid = order.paymentStatus?.value === 'paid';
  const returnedFromGateway = new URLSearchParams(location.search).get('payment') === 'complete';
  const stage: 'awaiting' | 'confirming' | 'confirmed' = paid
    ? 'confirmed'
    : returnedFromGateway
      ? 'confirming'
      : 'awaiting';


  /*
   | Formatted as an address, not dumped as a schema — see formatAddress.
   | Listing every field on its own line printed the city twice, echoed it
   | again as the region, repeated the postcode already inside an autofilled
   | line 1, and finished with the raw code "GB".
   */
  const addressLines = formatAddressLines(
    order.delivery.address ?? {},
    (code) => countries.find((entry) => entry.code === code)?.name,
  );

  return (
    <div className="pb-4">
      {/*
        No dark slab.
        ------------------------------------------------------------------
        A full-bleed black band is a lot of weight to spend on a message the
        customer reads once, and it pushes the thing they actually came for —
        the reference number and what they paid — below the fold. The moment
        is carried by the gold mark and the typography instead, on the same
        warm ground as the rest of the site, and the receipt starts almost
        immediately.
      */}
      <section className="relative overflow-hidden px-5 pb-10 pt-12 sm:px-6 sm:pt-14">
        {/* Faint gold bloom, only once there is something to celebrate. */}
        {stage === 'confirmed' && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-64"
            style={{
              background:
                'radial-gradient(40% 70% at 50% 0%, color-mix(in srgb, var(--color-accent) 16%, transparent) 0%, transparent 70%)',
            }}
          />
        )}

        <div className="relative mx-auto max-w-3xl">
          <StepRail stage={stage} />

          <div className={stage === 'confirmed' ? 'mt-10 text-center' : 'mt-10'}>
            {stage === 'confirmed' && (
              <motion.div {...rise(0)} className="flex justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                  <Check className="h-6 w-6 text-white" strokeWidth={2.5} aria-hidden="true" />
                </span>
              </motion.div>
            )}

            {stage !== 'confirmed' && (
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
                <p className="eyebrow !mt-0">
                  {stage === 'confirming' ? 'Confirming payment' : 'One step left'}
                </p>
              </div>
            )}

            <motion.h1
              {...rise(0.08)}
              className={`font-display leading-tight text-ink ${
                stage === 'confirmed'
                  ? 'mt-7 text-3xl sm:text-4xl'
                  : 'mt-4 text-[1.75rem] sm:text-3xl lg:text-[2.25rem]'
              }`}
            >
              {stage === 'confirmed'
                ? 'Thank you'
                : stage === 'confirming'
                  ? 'Confirming your payment'
                  : 'Your order is reserved'}
            </motion.h1>

            <motion.p
              {...rise(0.16)}
              className={`mt-3 text-sm leading-[1.8] text-muted ${
                stage === 'confirmed' ? 'mx-auto max-w-sm' : 'max-w-md'
              }`}
            >
              {stage === 'confirmed'
                ? order.status.description
                : stage === 'confirming'
                  ? 'We are checking with your payment provider. This usually takes a few seconds, and we will email you the moment it clears.'
                  : `We are holding ${order.items.length === 1 ? 'this piece' : 'these pieces'} for you. Your order is not confirmed until payment is complete.`}
            </motion.p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-5 sm:px-6">
        {/*
          Told once, at the moment it happened.
          ------------------------------------------------------------------
          Somebody who ticked "save my details" and then heard nothing would
          reasonably assume it had not worked. It also states plainly that no
          password was emailed, because an unexpected "your account is ready"
          message is otherwise easy to read as a credential loose in an inbox.
        */}
        {passed?.accountCreated && (
          <motion.div {...rise(0.18)} className="mb-8">
            <div className="flex items-start gap-3 border border-line bg-surface px-5 py-4">
              <UserRound
                className="mt-0.5 h-4 w-4 shrink-0 text-accent"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <p className="text-sm leading-[1.8] text-muted">
                <span className="text-ink">We have created your Glanz Luxe account.</span>{' '}
                This order is already saved to it. Check your inbox for a link to choose a
                password — we never send passwords by email.
              </p>
            </div>
          </motion.div>
        )}
        {/*
          Payment first, above the receipt.
          ------------------------------------------------------------------
          The order exists but is not settled, so the most useful thing on
          this page is the way to pay for it. Burying that under a summary of
          what was ordered would arrange the page around what we find
          interesting rather than what the customer needs next.
        */}
        {order.paymentStatus?.value !== 'paid' && (
          <motion.div {...rise(0.2)} className="mb-8">
            <PaymentPanel
              orderNumber={order.orderNumber}
              checkoutToken={checkoutToken}
            />
          </motion.div>
        )}

        <motion.div {...rise(0.24)} className="border border-line bg-surface shadow-card">
          {/* --------------------------------------------------- reference */}
          <div className="flex flex-col items-center gap-1 border-b border-line px-6 py-8 text-center sm:px-10">
            <span className="text-[0.6rem] uppercase tracking-[0.32em] text-muted">
              Order number
            </span>
            <span className="font-display text-2xl text-ink sm:text-3xl">{order.orderNumber}</span>

            {/* Only true once it IS confirmed. Promising a confirmation
                email for an unpaid order is the same lie as the tick. */}
            <span className="mt-3 flex items-center gap-1.5 text-center text-xs text-muted">
              <Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {stage === 'confirmed'
                ? 'A confirmation is on its way to your inbox.'
                : 'Keep this reference — you can return to this page to pay.'}
            </span>
          </div>

          {/* ------------------------------------------------------- items */}
          <div className="px-6 py-8 sm:px-10">
            <h2 className="text-[0.6rem] uppercase tracking-[0.32em] text-muted">Your order</h2>

            <ul className="mt-5 flex flex-col divide-y divide-line">
              {order.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-5 py-4 text-sm first:pt-0">
                  <div className="min-w-0">
                    <p className="text-ink">{item.name}</p>

                    <p className="mt-1 text-xs text-muted">
                      {item.variantName && <span>{item.variantName} · </span>}
                      Quantity {item.quantity}
                    </p>

                    {item.personalisation && Object.keys(item.personalisation).length > 0 && (
                      <dl className="mt-2 flex flex-col gap-0.5 border-l border-accent/40 pl-3 text-xs text-muted">
                        {Object.entries(item.personalisation).map(([key, value]) => (
                          <div key={key} className="flex gap-1.5">
                            <dt className="capitalize">{key.replace(/_/g, ' ')}:</dt>
                            <dd className="text-ink">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>

                  {item.lineTotal && (
                    <span className="shrink-0 text-ink">{item.lineTotal.formatted}</span>
                  )}
                </li>
              ))}
            </ul>

            {/* Absent entirely for a gift recipient — the server omits the
                money rather than trusting the UI to hide it (§45). */}
            {order.totals && (
              <dl className="mt-6 flex flex-col gap-2.5 border-t border-line pt-6 text-sm">
                <Row label="Subtotal" value={order.totals.subtotal.formatted} />
                <Row
                  label="Delivery"
                  value={
                    order.totals.shipping.minor === 0 ? 'Free' : order.totals.shipping.formatted
                  }
                />
                {order.totals.discount.minor > 0 && (
                  <Row label="Discount" value={`−${order.totals.discount.formatted}`} />
                )}
                {/* Only EXCLUSIVE tax belongs in a column that sums to the
                    total — inclusive tax is already inside it, and listing it
                    here makes correct arithmetic look wrong. */}
                {!order.totals.tax.inclusive && (
                  <Row
                    label={order.totals.tax.label ?? 'Tax'}
                    value={order.totals.tax.amount.formatted}
                  />
                )}

                <div className="mt-3 border-t border-line pt-5">
                  <div className="flex items-baseline justify-between">
                    <dt className="text-sm text-ink">Total</dt>
                    <dd className="font-display text-2xl text-ink">
                      {order.totals.total.formatted}
                    </dd>
                  </div>

                  {/* A breakdown of the total, not a footnote — see the note
                      in CheckoutPage: the customer must be able to see the tax
                      they paid at the same weight as every other line. */}
                  {order.totals.tax.inclusive && order.totals.tax.amount.minor > 0 && (
                    <div className="mt-2 flex items-baseline justify-between text-sm">
                      <span className="text-muted">
                        of which {order.totals.tax.label ?? 'tax'}
                      </span>
                      <span className="text-ink">{order.totals.tax.amount.formatted}</span>
                    </div>
                  )}
                </div>
              </dl>
            )}
          </div>

          {/* --------------------------------------- delivery + highlights */}
          <div className="grid gap-8 border-t border-line px-6 py-8 sm:grid-cols-2 sm:px-10">
            {addressLines.length > 0 && (
              <div>
                <h2 className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.32em] text-muted">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                  Delivering to
                </h2>

                <address className="mt-3 text-sm not-italic leading-relaxed text-ink">
                  {addressLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </address>
              </div>
            )}

            <div className="flex flex-col gap-5">
              {order.delivery.methodLabel && (
                <div>
                  <h2 className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.32em] text-muted">
                    <Truck className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    Delivery
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink">
                    {order.delivery.methodLabel}
                  </p>
                </div>
              )}

              {order.delivery.scheduledFor && (
                <div>
                  <h2 className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.32em] text-muted">
                    <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    Held for
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink">
                    {order.delivery.scheduledFor}
                  </p>
                </div>
              )}

              {order.gift.isGift && (
                <div>
                  <h2 className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.32em] text-muted">
                    <Gift className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    Gift
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink">
                    {order.gift.recipientName ? `For ${order.gift.recipientName}. ` : ''}
                    <span className="text-muted">
                      {order.gift.message
                        ? 'Your message will be handwritten onto the gift card.'
                        : 'Finished with our gift wrapping.'}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {order.customerNote && (
            <div className="border-t border-line px-6 py-6 sm:px-10">
              <h2 className="text-[0.6rem] uppercase tracking-[0.32em] text-muted">Your note</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">{order.customerNote}</p>
            </div>
          )}
        </motion.div>

        {/* ------------------------------------------------ what happens next */}
        <section aria-labelledby="next-heading" className="mt-14">
          <h2
            id="next-heading"
            className="text-center text-[0.6rem] uppercase tracking-[0.32em] text-muted"
          >
            What happens next
          </h2>

          <ol className="mt-8 grid gap-8 sm:grid-cols-3 sm:gap-6">
            {[
              {
                icon: CreditCard,
                title: order.paymentStatus.isSettled ? 'Payment received' : 'We take payment',
                /* Reads the real state rather than asserting one. It used to
                   say payment was unavailable, which stopped being true the
                   moment a gateway went live — copy that contradicts the
                   panel directly above it is worse than none (§86). */
                body: order.paymentStatus.description,
                now: ! order.paymentStatus.isSettled,
              },
              {
                icon: Package,
                title: 'We prepare it by hand',
                body: order.gift.isGift
                  ? 'Wrapped, finished and packed with your gift card.'
                  : 'Wrapped, finished and packed with care.',
              },
              {
                icon: Truck,
                title: order.delivery.scheduledFor ? 'We deliver on your date' : 'We dispatch',
                body: 'You will receive tracking by email.',
              },
            ].map((step, index) => (
              <li key={step.title} className="flex flex-col items-center gap-3 text-center">
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-full border ${
                    step.now ? 'border-accent bg-accent/10 text-accent' : 'border-line text-muted'
                  }`}
                >
                  <step.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                </span>

                <span className="text-[0.6rem] uppercase tracking-[0.28em] text-muted">
                  {String(index + 1).padStart(2, '0')}
                </span>

                <h3 className="font-display text-lg leading-snug text-ink">{step.title}</h3>
                <p className="max-w-[22ch] text-sm leading-relaxed text-muted">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ----------------------------------------------------------- actions */}
        <div className="mt-16 flex flex-col gap-3 border-t border-line pt-10 sm:flex-row sm:justify-center">
          <ButtonLink to="/shop" variant="secondary">
            Continue Browsing
          </ButtonLink>

          {/* Order history is behind ProtectedRoute, so offering it to a guest
              would land them on a login they never created (§86). */}
          {isAuthenticated ? (
            <ButtonLink to="/orders">View My Orders</ButtonLink>
          ) : (
            <ButtonLink to="/register">Create an Account</ButtonLink>
          )}
        </div>

        {!isAuthenticated && (
          <p className="mt-4 text-center text-xs leading-relaxed text-muted">
            Register with <span className="text-ink">the same email address</span> and this order
            will appear in your history once you confirm it.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * Where the customer is in the purchase — §41.
 *
 * Four steps, with the first two already behind them. Its real job is on the
 * unpaid states: it makes "there is something still to do" legible at a
 * glance, which a page that opens with a tick actively hides. It is also why
 * the customer can tell the difference between "reserved" and "bought"
 * without reading a word of body copy.
 */
function StepRail({ stage }: { stage: 'awaiting' | 'confirming' | 'confirmed' }) {
  const steps = [
    { label: 'Details', done: true },
    { label: 'Review', done: true },
    { label: 'Payment', done: stage === 'confirmed', current: stage !== 'confirmed' },
    { label: 'Confirmed', done: stage === 'confirmed', current: false },
  ];

  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
      {steps.map((step, index) => (
        <li key={step.label} className="flex items-center gap-3">
          <span
            className={[
              'flex items-center gap-2 whitespace-nowrap tracking-wide transition-colors',
              step.done
                ? 'text-accent'
                : step.current
                  ? 'text-ink'
                  : 'text-muted',
            ].join(' ')}
            aria-current={step.current ? 'step' : undefined}
          >
            <span
              aria-hidden="true"
              className={[
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[0.625rem]',
                step.done
                  ? 'border-accent bg-accent text-white'
                  : step.current
                    ? 'border-ink text-ink'
                    : 'border-line text-muted',
              ].join(' ')}
            >
              {step.done ? <Check className="h-3 w-3" strokeWidth={3} /> : index + 1}
            </span>

            {step.label}

            {/* The state is carried by colour and a tick, so it is also said
                in words for anyone who cannot see either. */}
            {step.current && <span className="sr-only">(current step)</span>}
          </span>

          {index < steps.length - 1 && (
            <span className="h-px w-4 bg-line sm:w-6" aria-hidden="true" />
          )}
        </li>
      ))}
    </ol>
  );
}
